package gr.tech.GRAttestation.pages.admin.stores

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.modals.StoreInputModal
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.ConfirmModal
import gr.tech.GRAttestation.components.widgets.NavBreadcrumb
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.infrastructure.api.adminCreateNewStore
import gr.tech.GRAttestation.infrastructure.api.adminDeleteStoreByID
import gr.tech.GRAttestation.infrastructure.api.adminFetchAllStores
import gr.tech.GRAttestation.infrastructure.api.adminUpdateStore
import gr.tech.GRAttestation.models.stores.STORE_STATUS
import gr.tech.GRAttestation.models.stores.StoreDTO
import gr.tech.GRAttestation.models.stores.blankStoreDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.sections.tables.AdminStoresTable
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.util.Constants
import gr.tech.GRAttestation.util.closeModal
import gr.tech.GRAttestation.util.showBootstrapModal
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page()
@Composable
fun AdminStoresPage() {
    AdminPageLayout() { context ->
        AdminStoresScreen(context)
    }
}

@Composable
fun AdminStoresScreen(context: PageContext) {
    val scope = rememberCoroutineScope()
    var triggerLaunchedEffect by remember { mutableStateOf(false) }
    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    val stores = remember { mutableStateListOf<StoreDTO>() }
    var storesTotalCount by remember { mutableStateOf(0) }
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (storesTotalCount + pageSize - 1) / pageSize
    var currentPage by remember { mutableStateOf(1) }

    // Filter Data
    var basicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var selectedDateColumnLabel by remember { mutableStateOf("Created At") }
    var selectedStoreStatus by remember { mutableStateOf(STORE_STATUS.ACTIVE) }

    // Modal Data
    var currentStoreInformation: StoreDTO by remember { mutableStateOf(blankStoreDTO) }
    var currentStoreInformationFormError by remember { mutableStateOf("") }
    var toDeleteStore by remember { mutableStateOf<StoreDTO?>(null) }
    var toRestoreStore by remember { mutableStateOf<StoreDTO?>(null) }

    LaunchedEffect(Unit, triggerLaunchedEffect, currentPage, basicFilters, selectedStoreStatus) {
        adminFetchAllStores(
            pageNumber = currentPage,
            pageSize = pageSize,
            storeStatus = selectedStoreStatus,
            basicFilters = basicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    stores.clear()
                    stores.addAll(response.data.items)
                    storesTotalCount = response.data.totalCount
                    if (stores.isEmpty()) { currentPage = 1 }
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading stores!", status = 0))
                }
            },
            onError = {
                console.log("Error with adminFetchAllProjects(): $it")
                alertManager.addAlert(UserAlert(message = "Error loading stores!", status = 0))
            }
        )
    }

    // Add New or Edit Store
    StoreInputModal(
        modalId = "AdminStoreInputModal",
        storeInformation = currentStoreInformation,
        formError = currentStoreInformationFormError,
        onClose = {
            currentStoreInformation = blankStoreDTO.copy(id = -2)
            closeModal("AdminStoreInputModal")
        },
        onSubmit = {
            scope.launch {
                currentStoreInformation = it
                currentStoreInformationFormError = " "
                if (it.id > 0) {
                    // This is an edit of a store
                    adminUpdateStore(
                        editedStore = it,
                        onError = {},
                        onSuccess = { apiResponse ->
                            if (apiResponse.success) {
                                triggerLaunchedEffect = !triggerLaunchedEffect
                                alertManager.addAlert(UserAlert(message = "Store Updated!", status = 2))
                                closeModal("AdminStoreInputModal")
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Updating Store", status = 0))
                                currentStoreInformationFormError = apiResponse.error?.message ?: "Error updating."
                            }
                        },
                    )
                } else {
                    // This is a new form
                    adminCreateNewStore(
                        storeDTO = it,
                        onError = {},
                        onSuccess = { apiResponse ->
                            if (apiResponse.success) {
                                triggerLaunchedEffect = !triggerLaunchedEffect
                                alertManager.addAlert(UserAlert(message = "Store Added!", status = 2))
                                closeModal("AdminStoreInputModal")
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Adding Store", status = 0))
                                currentStoreInformationFormError = apiResponse.error?.message ?: "Error creating."
                            }
                        },
                    )
                }
            }
        },
    )

    // Confirm Deletion Modal
    ConfirmModal(
        modalId = "adminConfirmDelete",
        confirmMessage = "Are you sure you want to delete this store?",
        onClose = {
            closeModal("adminConfirmDelete")
        },
        onConfirm = {
            toDeleteStore?.let {
                scope.launch {
                    adminDeleteStoreByID(
                        storeId = toDeleteStore!!.id,
                        onSuccess = {
                            if (it.success) {
                                alertManager.addAlert(UserAlert(message = "Store Deleted!", status = 2))
                                stores.removeAt(stores.indexOf(toDeleteStore))
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Deleting Store!", status = 0))
                            }
                        },
                        onError = {
                            alertManager.addAlert(UserAlert(message = "Error Deleting Store!", status = 0))
                        }
                    )
                    closeModal("adminConfirmDelete")
                }
            }
        }
    )

    // Confirm Restore Modal
    ConfirmModal(
        modalId = "adminConfirmRestore",
        confirmMessage = "Are you sure you want to restore this store?",
        onClose = {
            closeModal("adminConfirmRestore")
        },
        onConfirm = {
            toRestoreStore?.let {
                scope.launch {
                    adminDeleteStoreByID(
                        storeId = toRestoreStore!!.id,
                        restore = true,
                        onSuccess = {
                            if (it.success) {
                                alertManager.addAlert(UserAlert(message = "Store Restored!", status = 2))
                                stores.removeAt(stores.indexOf(toRestoreStore))
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Restoring Store!", status = 0))
                            }
                        },
                        onError = {
                            alertManager.addAlert(UserAlert(message = "Error Restoring Store!", status = 0))
                        }
                    )
                    closeModal("adminConfirmRestore")
                }
            }
        }
    )

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Theme.HalfWhite.rgb)
            .fillMaxSize()
    ) {
        NavBreadcrumb(ctx = context)

        // Top Row Buttons
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        )
        {
            Button(
                attrs = Modifier
                    .classNames("btn", "btn-primary")
                    .onClick {
                        scope.launch {
                            adminFetchAllStores(
                                pageNumber = currentPage,
                                pageSize = pageSize,
                                storeStatus = selectedStoreStatus,
                                basicFilters = basicFilters,
                                onResponse = { response ->
                                    if (response.success && response.data != null) {
                                        stores.clear()
                                        stores.addAll(response.data.items)
                                        storesTotalCount = response.data.totalCount
                                    }
                                },
                                onError = {
                                    console.log(it)
                                },
                            )
                        }
                    }
                    .margin(right = 16.px)
                    .toAttrs()
            ) {
                FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                Text("Refresh")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {}
                    .toAttrs{
                        attr("data-bs-toggle", "collapse")
                        attr("data-bs-target", "#adminstoresFilters")
                    }
            ) {
                FaFilter(modifier = Modifier.margin(right = 8.px))
                Text("Filters")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {
                        currentStoreInformation = blankStoreDTO.copy(id = -3)
                        showBootstrapModal("AdminStoreInputModal")
                    }
                    .toAttrs{}
            ) {
                FaPlus(modifier = Modifier.margin(right = 8.px))
                Text("New")
            }

            Spacer()

            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

        // Filters Section
        Column(
            modifier = Modifier.id("adminstoresFilters").classNames("collapse")
                .margin(top = 8.px, bottom = 16.px)
                .padding(8.px)
                .backgroundColor(Colors.WhiteSmoke)
                .borderRadius(8.px)
                .fillMaxWidth()
        )
        {
            // Your Filters Row
            Row(
                modifier = Modifier
                    .fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {

                // Dropdown for column
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .id("dateColumnDropdown")
                            .toAttrs {
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        Text(selectedDateColumnLabel)
                    }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        basicFilters = basicFilters.copy(
                                            dateColumn = "createdAt"
                                        )
                                        selectedDateColumnLabel = "Created At"
                                }.toAttrs()
                            ) {
                                Text("Created At")
                            }
                        }
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        basicFilters = basicFilters.copy(
                                            dateColumn = "updatedAt"
                                        )
                                        selectedDateColumnLabel = "Updated At"
                                    }
                                    .toAttrs()
                            ) {
                                Text("Updated At")
                            }
                        }
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = true
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("ASCENDING")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = false
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("DESCENDING")
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = true) }
                            .toAttrs()
                    ) {
                        Text("BEFORE")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = false) }
                            .toAttrs()
                    ) {
                        Text("AFTER")
                    }
                }

                // Date input
                Input(
                    type = InputType.Date,
                    attrs = Modifier
                        .classNames("form-control")
                        .width(200.px)
                        .toAttrs {
                            attr("value", basicFilters.dateString)
                            onChange {
                                console.log(it.value)
                                basicFilters = basicFilters.copy(
                                    dateString = it.value
                                )
                            }
                        }
                )

                Spacer()

                // Reset Filters button
                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .onClick {
                            basicFilters = BasicTableFilters()
                            selectedDateColumnLabel = "Created At"
                        }
                        .toAttrs{}
                ) {
                    FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                    Text("Reset")
                }
            }

            Row(
                modifier = Modifier
                    .margin(top = 16.px)
                    .fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .id("dateColumnDropdown")
                            .toAttrs {
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        Text(selectedStoreStatus.name)
                    }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        selectedStoreStatus = STORE_STATUS.ACTIVE
                                    }
                                    .toAttrs()
                            ) {
                                Text("Active")
                            }
                        }
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        selectedStoreStatus = STORE_STATUS.DELETED
                                    }
                                    .toAttrs()
                            ) {
                                Text("Deleted")
                            }
                        }
                    }
                }
            }
        }

        AdminStoresTable(
            stores = stores,
            onView = { store ->
                context.router.navigateTo(AdminSitePage.Stores.passStoreId(store.id.toString()))
             },
            onEdit = { store ->
                currentStoreInformation = store
                showBootstrapModal("AdminStoreInputModal")
            },
            onViewPDFs = { store ->
                showBootstrapModal("adminConfirmApproval")
            },
            onDelete = { store ->
                toDeleteStore = store
                showBootstrapModal("adminConfirmDelete")
            },
            onRestore = { store ->
                toRestoreStore = store
                showBootstrapModal("adminConfirmRestore")
            }
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

    }
}